// Modelo declarativo de secciones. Puro: recibe el árbol de rutas como
// argumento en vez de importarlo, para poder testearlo sin arrastrar .vue ni
// el store. El cableado con el árbol real vive en `nav-model.js`.
import {
  isPortalIncidentsUser, isLockedToPortal, normalizeCorporaciones,
} from '@/util/cad-incident-utils'
import { canAccess } from './access'

const hasCorporaciones = (user) => normalizeCorporaciones(
  (user && user.attributes && user.attributes.corporaciones) || [],
).length > 0

// Seis secciones. `names` enumera las rutas de la sección; `rootName` /
// `rootPath` delega en el subárbol, de modo que añadir una CRUD nueva la
// incluye automáticamente sin editar este archivo.
export const SECTION_DEFS = [
  {
    id: 'map', titleKey: 'map', icon: 'mdi-map', names: ['map']
  },
  {
    id: 'analytics',
    titleKey: 'analytics-section',
    icon: 'mdi-cctv',
    names: ['analytics-monitor', 'analytics-history'],
  },
  // La consola de monitoreo de medidas cautelares. `mc-monitor-persona` es la
  // misma vista con una persona preseleccionada, así que entra como ruta
  // secundaria de esta sección, igual que el histórico dentro de Analíticos.
  // El simulador NO entra: está oculto a propósito (ver route-coverage.test).
  {
    id: 'medidas-cautelares',
    titleKey: 'mc-section',
    icon: 'mdi-account-lock',
    names: ['mc-monitor', 'mc-monitor-persona'],
  },
  {
    id: 'reports', titleKey: 'reports', icon: 'mdi-file-chart', rootPath: '/reports'
  },
  {
    id: 'admin', titleKey: 'admin-section', icon: 'mdi-cog', rootName: 'admin'
  },
  {
    id: 'incident-portal',
    titleKey: 'incident-portal',
    icon: 'mdi-shield-alert-outline',
    names: ['incident-portal'],
  },
]

// Hoja = vista real. Misma definición que usa el test estático de rutas: con
// componente propio y sin hijos.
//
// Cada hoja se devuelve como `{ record, matched }` con su cadena de ancestros.
// Sin la cadena, un grupo gateado —Mando Único declara su permiso una sola vez,
// en el grupo— dejaría a sus hijos como "sin gate explícito" y la sección
// Administración no se le mostraría a un usuario que solo tiene `mando_unico`.
function collectLeaves(records, chain, out) {
  (records || []).forEach((r) => {
    const next = chain.concat([r])
    if (r.children && r.children.length) {
      collectLeaves(r.children, next, out)
      return
    }
    if (r.component) out.push({ record: r, matched: next })
  })
  return out
}

// Una hoja tiene gate explícito si ella o cualquiera de sus ancestros lo declara.
const tieneGateExplicito = (leaf) => leaf.matched.some(
  (r) => r.meta && (r.meta.admin || r.meta.validate),
)

function findRecord(tree, predicate) {
  for (const r of (tree || [])) {
    if (predicate(r)) return r
    if (r.children && r.children.length) {
      const hit = findRecord(r.children, predicate)
      if (hit) return hit
    }
  }
  return null
}

// Devuelve `[{ record, matched }]`. `matched` imita la cadena que Vue Router
// pone en `$route.matched`, para poder pasarla a `canAccess` tal cual.
export function sectionLeaves(tree, sectionId) {
  const def = SECTION_DEFS.find((s) => s.id === sectionId)
  if (!def) return []
  if (def.names) {
    return def.names
      .map((n) => findRecord(tree, (r) => r.name === n))
      .filter(Boolean)
      .map((r) => ({ record: r, matched: [r] }))
  }
  // La raíz de una sección debe tener hijos: `routes.js` tiene stubs de solo
  // redirect (p.ej. `/reports` dentro de `admin.children`, sin `children` ni
  // `component`) que comparten path/name con la shell real de la sección. Sin
  // esta condición, el DFS pre-order de `findRecord` se queda con el primer
  // duplicado que encuentra —el stub, que no tiene nada que coleccionar— antes
  // de llegar a la shell real, y `sectionLeaves` sale vacío para esa sección.
  const root = findRecord(
    tree,
    (r) => {
      const isRootMatch = def.rootName ? r.name === def.rootName : r.path === def.rootPath
      return isRootMatch && !!(r.children && r.children.length)
    },
  )
  return root ? collectLeaves(root.children, [root], []) : []
}

// Una sección con varias rutas es visible si el usuario es administrador o si
// al menos una de sus rutas con gate EXPLÍCITO le deja pasar.
//
// Deliberadamente NO se escanea `user.attributes` buscando claves
// `permission_*`: hay permisos que no corresponden a ninguna ruta de estas
// secciones (`permission_murphy`, `permission_reportantes`), y un operador con
// uno de ellos abriría una sección donde no tiene nada que hacer.
function hasGatedAccess(tree, sectionId, user) {
  if (!user) return false
  if (user.administrator) return true
  return sectionLeaves(tree, sectionId).some(
    (leaf) => tieneGateExplicito(leaf) && canAccess(leaf, user),
  )
}

// Destino de una sección: la primera ruta con gate explícito que el usuario
// pase, en orden de declaración de `routes.js`. Nunca una ruta sin gate.
export function firstAccessibleRouteIn(tree, sectionId, user) {
  const hit = sectionLeaves(tree, sectionId).find(
    (leaf) => tieneGateExplicito(leaf) && canAccess(leaf, user),
  )
  return hit ? { name: hit.record.name } : { name: 'map' }
}

function isVisible(tree, sectionId, user) {
  // El mapa es la sección de referencia y no lleva gate propio, pero un
  // usuario encerrado en el portal no debe verla: el guard lo devolvería al
  // portal, así que ofrecérsela es prometer una navegación que no ocurre.
  // Con esto el launcher se oculta solo en el portal (queda una sección) y
  // la vista no necesita taparlo con un v-if propio.
  if (sectionId === 'map') return !isLockedToPortal(user)
  if (user && user.administrator) return true
  if (sectionId === 'incident-portal') return isPortalIncidentsUser(user) || hasCorporaciones(user)
  if (sectionId === 'analytics') {
    const leaf = sectionLeaves(tree, 'analytics')[0]
    return !!leaf && canAccess(leaf, user)
  }
  return hasGatedAccess(tree, sectionId, user)
}

export function visibleSections(tree, user) {
  return SECTION_DEFS
    .filter((def) => isVisible(tree, def.id, user))
    .map((def) => ({
      id: def.id,
      titleKey: def.titleKey,
      icon: def.icon,
      route: (def.names && def.names.length === 1)
        ? { name: def.names[0] }
        : firstAccessibleRouteIn(tree, def.id, user),
    }))
}

// Sección activa a partir del nombre de ruta. Sin esto el launcher no puede
// marcar dónde está el usuario cuando se encuentra en una ruta secundaria
// (p.ej. el histórico dentro de Analíticos).
export function sectionForRoute(tree, routeName) {
  if (!routeName) return null
  const hit = SECTION_DEFS.find(
    (def) => sectionLeaves(tree, def.id).some((leaf) => leaf.record.name === routeName),
  )
  return hit ? hit.id : null
}
